/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { CaretDown, ClockCounterClockwise, User } from "@phosphor-icons/react";

/**
 * Phase 7 — Read-only audit-trail timeline.
 * Reads from existing audit_logs collection via existing endpoint.
 * Collapsible, hidden by default to keep doc viewer clean.
 */
const ACTION_LABEL = {
  create:   { label: "Created",       color: "bg-slate-100 text-slate-700" },
  update:   { label: "Updated",       color: "bg-blue-100 text-blue-700" },
  submit:   { label: "Submitted",     color: "bg-indigo-100 text-indigo-700" },
  approve:  { label: "Approved",      color: "bg-emerald-100 text-emerald-700" },
  reject:   { label: "Rejected",      color: "bg-rose-100 text-rose-700" },
  convert:  { label: "Converted",     color: "bg-violet-100 text-violet-700" },
  payment:  { label: "Payment",       color: "bg-emerald-100 text-emerald-700" },
  cancel:   { label: "Cancelled",     color: "bg-amber-100 text-amber-700" },
  fulfill:  { label: "Fulfilled",     color: "bg-teal-100 text-teal-700" },
  delete:   { label: "Deleted",       color: "bg-rose-100 text-rose-700" },
  default:  { label: "Activity",      color: "bg-slate-100 text-slate-600" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function BillingAuditTimeline({ entityType, entityId, role }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || logs !== null || !entityId) return;
    setLoading(true);
    // JWT-authenticated. Backend enforces admin/manager/cfo via require_admin_manager_cfo.
    api.get(`/audit-logs`, { params: { entity_type: entityType, entity_id: entityId } })
      .then(r => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open, logs, entityId, entityType, role]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl print:hidden" data-testid="billing-audit-timeline">
      <button
        onClick={() => setOpen(!open)}
        data-testid="audit-timeline-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-800">Activity Timeline</span>
          {logs && logs.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold bg-slate-100 text-slate-600 rounded-full">{logs.length}</span>
          )}
        </div>
        <CaretDown size={12} weight="bold" className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 max-h-72 overflow-y-auto" data-testid="audit-timeline-body">
          {loading ? (
            <div className="text-center text-xs text-slate-400 py-3">Loading…</div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-3">No activity recorded yet.</div>
          ) : (
            <ol className="space-y-2.5">
              {logs.map((log, i) => {
                const meta = ACTION_LABEL[log.action] || ACTION_LABEL.default;
                return (
                  <li key={log.audit_id || i} className="flex items-start gap-3 text-xs" data-testid={`audit-row-${i}`}>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${meta.color} shrink-0`}>{meta.label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-700">
                        {log.actor_name || log.actor_id || "System"}
                        {log.actor_role && <span className="text-slate-400 ml-1">· {log.actor_role}</span>}
                      </div>
                      <div className="text-slate-500">{fmtDate(log.created_at)}</div>
                      {log.notes && <div className="text-slate-600 mt-0.5">{log.notes}</div>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
